/**
 * Centralised pickers for Project Name and Client (Company) fields.
 *
 * Three flavours:
 *   - <CompanyCombobox>      → Search existing CRM companies OR keep free-text.
 *                              Used for "Client" / "Client Name" anywhere the
 *                              user might type a brand-new client.
 *
 *   - <ProjectCombobox>      → Search existing projects + past enquiry/opp
 *                              project names OR keep free-text. Used in
 *                              tendering forms where the project may not
 *                              exist yet.
 *
 *   - <ProjectStrictPicker>  → Strict pick from ACTIVE projects only. No
 *                              free-text. Used downstream (Fuel master,
 *                              Mobile SVR, etc.) where the project must
 *                              already exist.
 *
 * All three return a plain string (`name`) to the caller so they slot into
 * existing free-text bindings without schema changes. StrictPicker can also
 * surface the picked project's `id` via the optional `onPick(project)` cb.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, ChevronDown, Plus } from 'lucide-react';
import { apiGet } from '../../lib/api';

// ── Module-level caches so a page doesn't re-fetch on every modal open ──
let _companyCache = null;
let _projectCache = null;
let _enquiryNamesCache = null;
let _oppNamesCache = null;

export const refreshPickerCaches = () => {
  _companyCache = null;
  _projectCache = null;
  _enquiryNamesCache = null;
  _oppNamesCache = null;
};

async function loadCompanies() {
  if (_companyCache) return _companyCache;
  try {
    const r = await apiGet('/crm/companies?scope=all');
    _companyCache = (r?.companies || []).map((c) => ({ id: c.id, name: c.name }));
  } catch { _companyCache = []; }
  return _companyCache;
}

async function loadProjects() {
  if (_projectCache) return _projectCache;
  try {
    // include_inactive so the tendering combobox sees historical projects too.
    const r = await apiGet('/projects?include_inactive=true&limit=500');
    _projectCache = (Array.isArray(r) ? r : []).map((p) => ({ id: p.id, name: p.name, site_code: p.site_code, status: p.status }));
  } catch { _projectCache = []; }
  return _projectCache;
}

async function loadActiveProjects() {
  // Always-fresh (no cache) — caller usually wants live active list.
  try {
    const r = await apiGet('/projects?limit=500');
    return (Array.isArray(r) ? r : []).map((p) => ({ id: p.id, name: p.name, site_code: p.site_code, status: p.status }));
  } catch { return []; }
}

async function loadEnquiryNames() {
  if (_enquiryNamesCache) return _enquiryNamesCache;
  try {
    const r = await apiGet('/enquiries');
    _enquiryNamesCache = Array.from(new Set((r?.items || []).map((e) => e.project_name).filter(Boolean)));
  } catch { _enquiryNamesCache = []; }
  return _enquiryNamesCache;
}

async function loadOpportunityNames() {
  if (_oppNamesCache) return _oppNamesCache;
  try {
    const r = await apiGet('/crm/opportunities');
    const list = r?.opportunities || r?.items || (Array.isArray(r) ? r : []);
    _oppNamesCache = Array.from(new Set(list.map((o) => o.project_name).filter(Boolean)));
  } catch { _oppNamesCache = []; }
  return _oppNamesCache;
}

// ─────────────────────────────────────────────────────────────────────
// Shared combobox primitive — type to filter, click to pick, free-text
// fallback if `allowFreeText` is true.
// ─────────────────────────────────────────────────────────────────────
function Combobox({
  value,
  onChange,
  options,
  placeholder = 'Search…',
  allowFreeText = false,
  testid,
  required = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // When the dropdown is closed, the display value mirrors `value`.
  // When open, the user is typing into `query`.
  useEffect(() => { if (!open) setQuery(''); }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return options.slice(0, 100);
    return options.filter((o) => (o.label || '').toLowerCase().includes(q)).slice(0, 100);
  }, [options, q]);

  const exactMatch = useMemo(
    () => options.find((o) => (o.label || '').toLowerCase() === q),
    [options, q]
  );

  const pick = (label) => {
    onChange(label || '');
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 border rounded-sm text-sm bg-white text-left hover:border-[#3b4872] ${
          open ? 'border-[#3b4872] ring-1 ring-[#3b4872]' : 'border-slate-300'
        } ${required && !value ? 'border-rose-300' : ''}`}
        data-testid={testid}
      >
        <span className={value ? 'text-slate-900 truncate' : 'text-slate-400 truncate'}>
          {value || placeholder}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {value && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              className="p-0.5 hover:bg-slate-200 rounded-sm"
              aria-label="Clear"
            >
              <X size={12} className="text-slate-400" />
            </span>
          )}
          <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg overflow-hidden">
          <div className="border-b border-slate-200 px-2.5 py-2 flex items-center gap-1.5 bg-slate-50">
            <Search size={12} className="text-slate-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="flex-1 bg-transparent text-sm outline-none"
              data-testid={testid ? `${testid}-input` : undefined}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setOpen(false);
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (filtered.length) pick(filtered[0].label);
                  else if (allowFreeText && query.trim()) pick(query.trim());
                }
              }}
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} className="text-slate-400 hover:text-slate-700">
                <X size={12} />
              </button>
            )}
          </div>
          <ul className="max-h-64 overflow-y-auto py-1 text-sm">
            {allowFreeText && query.trim() && !exactMatch && (
              <li>
                <button
                  type="button"
                  onClick={() => pick(query.trim())}
                  className="w-full text-left px-3 py-2 hover:bg-emerald-50 flex items-center gap-2 text-emerald-700 border-b border-slate-100"
                  data-testid={testid ? `${testid}-add-new` : undefined}
                >
                  <Plus size={12} />
                  <span>Add new: <strong>{query.trim()}</strong></span>
                </button>
              </li>
            )}
            {filtered.length === 0 && !(allowFreeText && query.trim()) ? (
              <li className="px-3 py-4 text-center text-xs text-slate-400">
                {q ? `No matches for "${query}"` : 'No options'}
              </li>
            ) : (
              filtered.map((o) => (
                <li key={o.key}>
                  <button
                    type="button"
                    onClick={() => pick(o.label)}
                    className={`w-full text-left px-3 py-2 hover:bg-indigo-50 ${
                      o.label === value ? 'bg-indigo-50/60 font-semibold text-[#3b4872]' : 'text-slate-700'
                    }`}
                  >
                    {o.label}
                    {o.hint && <span className="text-[10px] text-slate-400 ml-1">· {o.hint}</span>}
                  </button>
                </li>
              ))
            )}
          </ul>
          {options.length > 100 && !q && (
            <div className="px-3 py-1.5 border-t border-slate-200 text-[10px] text-slate-400 bg-slate-50">
              Showing first 100 of {options.length}. Type to narrow down.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// CompanyCombobox — Client field, backed by CRM Companies master.
// ─────────────────────────────────────────────────────────────────────
export function CompanyCombobox({ value, onChange, placeholder = 'Search clients (companies)…', testid = 'company-combobox', required = false }) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    loadCompanies().then((cos) => setOpts(cos.map((c) => ({ key: c.id, label: c.name }))));
  }, []);
  return (
    <Combobox
      value={value}
      onChange={onChange}
      options={opts}
      placeholder={placeholder}
      allowFreeText={true}
      testid={testid}
      required={required}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────
// ProjectCombobox — project-name field.
//   source="projects"  (default): existing projects + past enquiries +
//                       opportunities. Used by the BD opportunity form.
//   source="enquiries":  PAST ENQUIRY project names ONLY — never active
//                       projects. An enquiry is a PRE-AWARD record; a project
//                       only exists once work is awarded.
// ─────────────────────────────────────────────────────────────────────
export function ProjectCombobox({ value, onChange, placeholder, testid = 'project-combobox', required = false, source = 'projects' }) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    let cancelled = false;
    if (source === 'enquiries') {
      apiGet('/enquiries/project-name-suggest')
        .then((r) => { if (!cancelled) setOpts((r?.items || []).map((n) => ({ key: `enq-${n}`, label: n, hint: 'Past enquiry' }))); })
        .catch(() => { if (!cancelled) setOpts([]); });
      return () => { cancelled = true; };
    }
    Promise.all([loadProjects(), loadEnquiryNames(), loadOpportunityNames()]).then(([projs, enq, opp]) => {
      if (cancelled) return;
      const seen = new Set();
      const out = [];
      projs.forEach((p) => {
        if (!p.name || seen.has(p.name.toLowerCase())) return;
        seen.add(p.name.toLowerCase());
        out.push({ key: `prj-${p.id}`, label: p.name, hint: p.status || 'Project' });
      });
      enq.forEach((n) => {
        if (!n || seen.has(n.toLowerCase())) return;
        seen.add(n.toLowerCase());
        out.push({ key: `enq-${n}`, label: n, hint: 'Past enquiry' });
      });
      opp.forEach((n) => {
        if (!n || seen.has(n.toLowerCase())) return;
        seen.add(n.toLowerCase());
        out.push({ key: `opp-${n}`, label: n, hint: 'Past opportunity' });
      });
      setOpts(out);
    });
    return () => { cancelled = true; };
  }, [source]);
  const ph = placeholder || (source === 'enquiries'
    ? 'Type the project being enquired about…'
    : 'Search projects / past enquiries…');
  return (
    <Combobox
      value={value}
      onChange={onChange}
      options={opts}
      placeholder={ph}
      allowFreeText={true}
      testid={testid}
      required={required}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────
// CategoryMultiCombobox — multi-select discipline picker for the enquiry
// form. Search / multi-select / add-new (free-text). Suggestions grow from
// past enquiries via /enquiries/category-suggest. Value is a string[].
// ─────────────────────────────────────────────────────────────────────
export function CategoryMultiCombobox({ value = [], onChange, placeholder = 'Search or add disciplines (Civil, PEB, MEP…)', testid = 'category-multi' }) {
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);
  const selected = Array.isArray(value) ? value : (value ? [value] : []);

  useEffect(() => {
    apiGet('/enquiries/category-suggest').then((r) => setOpts(r?.items || [])).catch(() => setOpts([]));
  }, []);
  useEffect(() => {
    if (!open) { setQuery(''); return; }
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    setTimeout(() => inputRef.current?.focus(), 0);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const has = (c) => selected.some((s) => s.toLowerCase() === c.toLowerCase());
  const add = (c) => {
    const v = (c || '').trim();
    if (!v || has(v)) { setQuery(''); return; }
    onChange([...selected, v]);
    setQuery('');
  };
  const remove = (c) => onChange(selected.filter((s) => s !== c));

  const q = query.trim().toLowerCase();
  const filtered = opts.filter((o) => !has(o) && (!q || o.toLowerCase().includes(q))).slice(0, 50);
  const showAdd = q && !opts.some((o) => o.toLowerCase() === q) && !has(query.trim());

  return (
    <div ref={ref} className="relative">
      <div
        onClick={() => setOpen(true)}
        className={`w-full min-h-[40px] flex flex-wrap items-center gap-1.5 px-2 py-1.5 border rounded-sm bg-white cursor-text ${open ? 'border-[#3b4872] ring-1 ring-[#3b4872]' : 'border-slate-300'}`}
        data-testid={testid}
      >
        {selected.map((c) => (
          <span key={c} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#3b4872]/10 text-[#3b4872] rounded-sm text-xs font-medium" data-testid={`${testid}-chip-${c.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
            {c}
            <span role="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); remove(c); }} className="hover:bg-[#3b4872]/20 rounded-sm" aria-label={`Remove ${c}`}>
              <X size={11} />
            </span>
          </span>
        ))}
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); if (filtered.length) add(filtered[0]); else if (query.trim()) add(query.trim()); }
            if (e.key === 'Backspace' && !query && selected.length) remove(selected[selected.length - 1]);
          }}
          placeholder={selected.length ? '' : placeholder}
          className="flex-1 min-w-[120px] bg-transparent text-sm outline-none py-0.5"
          data-testid={`${testid}-input`}
        />
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </div>
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg overflow-hidden">
          <ul className="max-h-56 overflow-y-auto py-1 text-sm">
            {showAdd && (
              <li>
                <button type="button" onClick={() => add(query.trim())} className="w-full text-left px-3 py-2 hover:bg-emerald-50 flex items-center gap-2 text-emerald-700 border-b border-slate-100" data-testid={`${testid}-add-new`}>
                  <Plus size={13} /> Add new: <b>{query.trim()}</b>
                </button>
              </li>
            )}
            {filtered.map((o) => (
              <li key={o}>
                <button type="button" onClick={() => add(o)} className="w-full text-left px-3 py-2 hover:bg-slate-50" data-testid={`${testid}-opt-${o.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  {o}
                </button>
              </li>
            ))}
            {!filtered.length && !showAdd && <li className="px-3 py-2 text-slate-400">All disciplines selected</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// LocationCombobox — single Location field for BD opportunity / enquiry
// forms. Search/select from real usage history (sourced from
// /masters/locations/suggest) OR add a brand-new location (free-text).
// The list grows organically as BD enters locations at the source.
// ─────────────────────────────────────────────────────────────────────
async function loadLocations() {
  try {
    const r = await apiGet('/masters/locations/suggest');
    return r?.items || [];
  } catch { return []; }
}

export function LocationCombobox({ value, onChange, placeholder = 'Search or add a location…', testid = 'location-combobox', required = false }) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    loadLocations().then((locs) => setOpts(locs.map((l) => ({ key: l, label: l }))));
  }, []);
  return (
    <Combobox
      value={value}
      onChange={onChange}
      options={opts}
      placeholder={placeholder}
      allowFreeText={true}
      testid={testid}
      required={required}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────
// NatureCombobox — editable Nature-of-Project picker for the enquiry form
// (#24). Search / select / add-new (free-text). Suggestions grow from
// distinct nature_of_project values across past enquiries via
// /enquiries/nature-suggest.
// ─────────────────────────────────────────────────────────────────────
async function loadNatureSuggestions() {
  try {
    const r = await apiGet('/enquiries/nature-suggest');
    return r?.items || [];
  } catch { return []; }
}

export function NatureCombobox({ value, onChange, placeholder = 'Search or add nature of project…', testid = 'nature-combobox', required = false }) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    loadNatureSuggestions().then((vals) => setOpts(vals.map((v) => ({ key: v, label: v }))));
  }, []);
  return (
    <Combobox
      value={value}
      onChange={onChange}
      options={opts}
      placeholder={placeholder}
      allowFreeText={true}
      testid={testid}
      required={required}
    />
  );
}
// downstream forms (Fuel master, Mobile SVR, etc.) where the project
// must already exist.
// ─────────────────────────────────────────────────────────────────────
export function ProjectStrictPicker({ value, onChange, onPick, placeholder = 'Select active project…', testid = 'project-strict-picker', required = false }) {
  const [opts, setOpts] = useState([]);
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    loadActiveProjects().then((projs) => {
      setProjects(projs);
      setOpts(projs.map((p) => ({ key: p.id, label: p.name, hint: p.site_code || null })));
    });
  }, []);

  const handleChange = (label) => {
    onChange(label || '');
    if (onPick) {
      const picked = projects.find((p) => p.name === label);
      onPick(picked || null);
    }
  };

  return (
    <Combobox
      value={value}
      onChange={handleChange}
      options={opts}
      placeholder={placeholder}
      allowFreeText={false}
      testid={testid}
      required={required}
    />
  );
}
